import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Tag, X } from 'lucide-react';
import { CheckoutElementsProvider, useCheckoutElements, PaymentElement } from '@stripe/react-stripe-js/checkout';
import stripeService, { getStripe } from '../services/stripe';
import { getCurrentUser } from '../services/auth';
import { stripePlans } from '../config/stripePlans';
import { ThemeContext } from '../context/ThemeContext';
import PoweredByStripe from '../components/PoweredByStripe';

const inputClass = 'block w-full px-3 py-2 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600';

// Module-level cache so StrictMode double-mount / quick remounts reuse one session
// instead of creating a duplicate Checkout Session on every render.
const sessionCache = new Map();
const sessionKey = (plan) => `${getCurrentUser()?.id}:${plan.priceId}`;
function getSessionPromise(plan) {
  const key = sessionKey(plan);
  if (!sessionCache.has(key)) {
    const user = getCurrentUser();
    const promise = stripeService.createCheckoutSession(plan.priceId, user.id).catch((err) => {
      sessionCache.delete(key);
      throw err;
    });
    sessionCache.set(key, promise);
  }
  return sessionCache.get(key);
}
// Drop a cached session that the Elements provider could not load (expired/completed/
// invalid clientSecret) so the next attempt creates a fresh one instead of replaying it.
function clearSessionForPlan(plan) {
  sessionCache.delete(sessionKey(plan));
}

const ErrorBanner = ({ message }) => message ? (
  <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4">
    <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
  </div>
) : null;

const cadenceLabel = (recurring) => {
  if (!recurring) return '';
  const { interval, intervalCount } = recurring;
  if (intervalCount === 1) return `per ${interval}`;
  return `every ${intervalCount} ${interval}s`;
};

const CheckoutSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse space-y-4">
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
  </div>
);

const ErrorPanel = ({ message, onRetry }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
    <p className="text-red-600 dark:text-red-400 mb-4">{message}</p>
    <div className="flex items-center justify-center gap-4">
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
        >
          Try again
        </button>
      )}
      <Link
        to="/account/subscription"
        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
      >
        Return to subscription page
      </Link>
    </div>
  </div>
);

const OrderSummary = ({ checkout, plan }) => {
  const lineItem = checkout.lineItems[0];
  const hasDiscount = checkout.total.discount.minorUnitsAmount > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 md:sticky md:top-24">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">
        Order summary
      </h2>

      <div className="flex justify-between items-start mb-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{lineItem?.name || 'Stonewall Invoicing'}</p>
        <p className="text-sm text-gray-900 dark:text-white">{lineItem?.subtotal?.amount}</p>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        {plan.name} plan · billed {cadenceLabel(lineItem?.recurring)}
      </p>

      {plan.savings && (
        <p className="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400 mb-4">
          <CheckCircle className="w-3.5 h-3.5 mr-1" />
          {plan.savings}
        </p>
      )}

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
          <span>Subtotal</span>
          <span>{checkout.total.subtotal.amount}</span>
        </div>
        {(checkout.discountAmounts || []).map((discount, i) => (
          <div key={i} className="flex justify-between text-sm text-green-600 dark:text-green-400">
            <span>{discount.displayName}</span>
            <span>−{discount.amount}</span>
          </div>
        ))}
        {hasDiscount && !checkout.discountAmounts?.length && (
          <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
            <span>Discount</span>
            <span>−{checkout.total.discount.amount}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-700 pt-2">
          <span>Total due today</span>
          <span>{checkout.total.total.amount}</span>
        </div>
      </div>
    </div>
  );
};

const PromoCodeInput = ({ checkout, disabled }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [promoError, setPromoError] = useState(null);

  const appliedPromo = (checkout.discountAmounts || []).find((d) => d.promotionCode !== null);

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim() || applying) return;
    setApplying(true);
    setPromoError(null);
    const result = await checkout.applyPromotionCode(code.trim());
    if (result.type === 'error') {
      setPromoError(result.error.message);
    } else {
      setCode('');
      setOpen(false);
    }
    setApplying(false);
  };

  const remove = async () => {
    if (applying) return;
    setApplying(true);
    await checkout.removePromotionCode();
    setApplying(false);
  };

  if (appliedPromo) {
    return (
      <div className="flex items-center justify-between rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-3 py-2">
        <span className="inline-flex items-center text-sm text-green-700 dark:text-green-300">
          <Tag className="w-4 h-4 mr-2" />
          {appliedPromo.displayName} applied
        </span>
        <button
          type="button"
          onClick={remove}
          disabled={disabled || applying}
          className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 disabled:opacity-50"
          aria-label="Remove promotion code"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 disabled:opacity-50"
      >
        + Add promotion code
      </button>
    );
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Promotion code"
          className={inputClass}
          disabled={disabled || applying}
          autoFocus
        />
        <button
          type="button"
          onClick={apply}
          disabled={disabled || applying || !code.trim()}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {applying ? 'Applying…' : 'Apply'}
        </button>
      </div>
      {promoError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{promoError}</p>}
    </div>
  );
};

const CheckoutForm = ({ plan, showPromo, onProviderError }) => {
  const checkoutState = useCheckoutElements();
  const [submitting, setSubmitting] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const checkout = checkoutState.type === 'success' ? checkoutState.checkout : null;
  const user = getCurrentUser();
  const email = checkout?.email || user?.email;

  useEffect(() => {
    if (checkout && !checkout.email && user?.email) {
      checkout.updateEmail(user.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout?.email]);

  // If the session couldn't load (expired/completed/invalid), drop it from the cache
  // so the user's "Try again" creates a fresh session rather than replaying the dead one.
  useEffect(() => {
    if (checkoutState.type === 'error') clearSessionForPlan(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutState.type]);

  if (checkoutState.type === 'loading') return <CheckoutSkeleton />;
  if (checkoutState.type === 'error') {
    return (
      <ErrorPanel
        message="We couldn't load the payment form. Please try again."
        onRetry={onProviderError}
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setConfirmError(null);
    const result = await checkout.confirm();
    if (result.type === 'error') {
      setConfirmError(result.error.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="grid md:grid-cols-5 gap-6 items-start">
      <form onSubmit={handleSubmit} className="md:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
          <p className={`${inputClass} text-gray-600 dark:text-gray-300`}>{email}</p>
        </div>

        <PaymentElement
          options={{
            layout: { type: 'accordion', defaultCollapsed: false, radios: 'never' },
            wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
            terms: { card: 'never' }
          }}
          onChange={(e) => setPaymentComplete(e.complete)}
        />

        {showPromo && <PromoCodeInput checkout={checkout} disabled={submitting} />}

        <ErrorBanner message={confirmError || checkout.lastPaymentError?.message} />

        <button
          type="submit"
          disabled={submitting || !paymentComplete || !checkout.canConfirm}
          className="w-full flex justify-center items-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              Processing…
            </>
          ) : (
            `Pay ${checkout.total.total.amount}`
          )}
        </button>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          By subscribing, you authorize Stonewall Invoicing to charge you according to the terms until you cancel.
        </p>

        <PoweredByStripe className="pt-1" />
      </form>

      <div className="md:col-span-2">
        <OrderSummary checkout={checkout} plan={plan} />
      </div>
    </div>
  );
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier');
  const plan = stripePlans[tier];
  const { darkMode } = useContext(ThemeContext);
  const [apiError, setApiError] = useState(null);
  const [referralDiscountApplied, setReferralDiscountApplied] = useState(false);
  // Bumping this forces a fresh provider + session after a load failure ("Try again").
  const [attempt, setAttempt] = useState(0);

  const stripeRef = useRef(null);
  if (!stripeRef.current) stripeRef.current = getStripe();

  const sessionRef = useRef(null);
  if (!sessionRef.current && plan) {
    sessionRef.current = getSessionPromise(plan)
      .then((session) => {
        setReferralDiscountApplied(!!session.referralDiscountApplied);
        return session.clientSecret;
      })
      .catch((err) => {
        setApiError(err.response?.data?.message || 'Failed to start checkout. Please try again.');
        return new Promise(() => {});
      });
  }

  const handleProviderError = () => {
    clearSessionForPlan(plan);
    sessionRef.current = null;
    setApiError(null);
    setAttempt((n) => n + 1);
  };

  const appearance = useMemo(() => ({
    theme: darkMode ? 'night' : 'stripe',
    labels: 'above',
    variables: {
      colorPrimary: '#2563EB',
      colorBackground: darkMode ? '#374151' : '#FFFFFF',
      colorText: darkMode ? '#F9FAFB' : '#111827',
      colorTextSecondary: darkMode ? '#9CA3AF' : '#6B7280',
      colorTextPlaceholder: '#9CA3AF',
      colorDanger: darkMode ? '#F87171' : '#DC2626',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSizeBase: '14px',
      borderRadius: '8px',
      focusBoxShadow: '0 0 0 2px #3B82F6'
    },
    rules: {
      '.Input': { borderColor: darkMode ? '#4B5563' : '#D1D5DB' },
      // blend the accordion container into the surrounding card
      '.AccordionItem': {
        backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
        border: 'none',
        boxShadow: 'none',
        paddingLeft: '0px',
        paddingRight: '0px'
      }
    }
  }), [darkMode]);

  if (!plan) {
    return <Navigate to="/account/subscription" replace />;
  }

  return (
    <div className="flex flex-col flex-1 bg-gray-100 dark:bg-gray-900 color-transition">
      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/account/subscription"
            className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to plans
          </Link>

          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Complete your purchase
            <span className="block text-sm font-normal text-gray-500 dark:text-gray-400">{plan.name} plan</span>
          </h1>

          {apiError ? (
            <ErrorPanel message={apiError} onRetry={handleProviderError} />
          ) : (
            <CheckoutElementsProvider
              key={attempt}
              stripe={stripeRef.current}
              options={{
                clientSecret: sessionRef.current,
                elementsOptions: { appearance, savedPaymentMethod: { enableSave: 'never', enableRedisplay: 'never' } }
              }}
            >
              <CheckoutForm plan={plan} showPromo={!referralDiscountApplied} onProviderError={handleProviderError} />
            </CheckoutElementsProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
